import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtInt } from "@/lib/api";
import { Plus, MagnifyingGlass, ArrowsClockwise, UserList, Trash, PencilSimple, X, CheckCircle, ArrowRight, FilePdf, FileXls, MapPin } from "@phosphor-icons/react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import HintButton from "@/components/HintButton";
import CustomerCardMobile from "@/components/customers/CustomerCardMobile";

const EMPTY = { name: "", document: "", email: "", phone: "", cellphone: "", address: "", address_number: "", complement: "", neighborhood: "", city: "", state: "", zipcode: "", notes: "" };

// Palette contrastante para até 27 UFs
const UF_COLORS = ["#002FA7", "#E60000", "#FFC000", "#00A300", "#8B00FF", "#FF6B00", "#00B8D9", "#E91E63", "#7B1FA2", "#546E7A", "#00796B", "#F57F17", "#5D4037", "#37474F", "#C62828", "#1565C0", "#2E7D32", "#EF6C00", "#4527A0", "#00838F", "#AD1457", "#4E342E", "#283593", "#558B2F", "#6A1B9A", "#EC407A", "#455A64"];

export default function Customers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [byState, setByState] = useState({ total: 0, by_state: [] });
  // A tela começa MOSTRANDO o gráfico e ESCONDENDO a lista de clientes.
  // Ao clicar em "Filtrar" (ou selecionar uma UF no ranking) trocamos:
  // o gráfico some e a lista dos clientes filtrados aparece.
  const [showResults, setShowResults] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (cityFilter) params.city = cityFilter;
      if (stateFilter) params.state = stateFilter;
      const { data } = await api.get("/customers", { params });
      setItems(data);
    } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/customers/stats/by-state");
      setByState(data);
    } catch { console.warn("customers/stats/by-state indisponível"); }
  };

  // No mount só carrega o gráfico. A lista só é buscada quando o usuário
  // aplica um filtro (evita descarregar milhares de linhas de uma vez).
  useEffect(() => { loadStats(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilter = async () => {
    setShowResults(true);
    await load();
  };

  const sync = async () => {
    setSyncing(true);
    try { await api.post("/customers/sync"); setTimeout(() => { load(); loadStats(); }, 2000); }
    catch (e) { alert(e?.response?.data?.detail || "Conecte o Bling primeiro"); }
    finally { setSyncing(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = (c) => {
    setForm({ name: c.name || "", document: c.document || "", email: c.email || "", phone: c.phone || "", cellphone: c.cellphone || "", address: c.address || "", address_number: c.address_number || "", complement: c.complement || "", neighborhood: c.neighborhood || "", city: c.city || "", state: c.state || "", zipcode: c.zipcode || "", notes: c.notes || "" });
    setEditing(c);
    setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing === "new") await api.post("/customers", form);
      else await api.put(`/customers/${editing.id}`, form);
      setEditing(null);
      if (showResults) load();
      loadStats();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const del = async (id) => {
    if (!confirm("Remover cliente?")) return;
    await api.delete(`/customers/${id}`);
    if (showResults) load();
    loadStats();
  };

  const exportFile = async (format) => {
    const params = new URLSearchParams();
    params.set("format", format);
    if (search) params.set("search", search);
    if (cityFilter) params.set("city", cityFilter);
    if (stateFilter) params.set("state", stateFilter);
    try {
      const res = await api.get(`/customers/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `clientes.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Erro ao exportar");
    }
  };

  const chartData = useMemo(() => {
    // Mostra top 10 UFs individualmente e agrupa o restante em "Outros"
    const list = byState.by_state || [];
    if (list.length <= 10) return list.map((r) => ({ name: r.state, value: r.count, pct: r.pct }));
    const top = list.slice(0, 10);
    const rest = list.slice(10);
    const restSum = rest.reduce((s, r) => s + r.count, 0);
    const restPct = byState.total ? +(100 * restSum / byState.total).toFixed(1) : 0;
    return [
      ...top.map((r) => ({ name: r.state, value: r.count, pct: r.pct })),
      { name: `+${rest.length} outros`, value: restSum, pct: restPct },
    ];
  }, [byState]);

  const clearFilters = () => {
    setSearch(""); setCityFilter(""); setStateFilter("");
    setShowResults(false);
    setItems([]);
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500 mb-2">BASE DE CLIENTES</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Cadastro de Clientes</h1>
          <HintButton text="Distribuição por estado, filtros e exportação para PDF/Excel." />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className="btn btn-ghost" onClick={openNew} data-testid="add-customer"><Plus size={16} weight="bold" /> Novo Cliente</button>
          <button className="btn btn-klein" onClick={sync} disabled={syncing} data-testid="sync-customers"><ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} /> {syncing ? "Sincronizando..." : "Importar do Bling"}</button>
        </div>
      </div>

      {/* Filtros + export — SEMPRE VISÍVEIS no topo (acima do gráfico) */}
      <div className="border border-black bg-white p-4 mb-4 flex items-end gap-3 flex-wrap" data-testid="filter-bar">
        <div className="flex-1 min-w-[220px]">
          <label className="label">Nome / Documento / Email</label>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input className="input pl-10" placeholder="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyFilter()} data-testid="cust-search" />
          </div>
        </div>
        <div className="w-40">
          <label className="label">Cidade</label>
          <input className="input" placeholder="ex: São Paulo" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyFilter()} data-testid="cust-city" />
        </div>
        <div className="w-24">
          <label className="label">UF</label>
          <input className="input" maxLength="2" placeholder="SP" value={stateFilter} onChange={(e) => setStateFilter(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && applyFilter()} data-testid="cust-state-filter" />
        </div>
        <div className="flex items-center gap-2">
          <button className="btn" onClick={applyFilter} data-testid="apply-filters"><MagnifyingGlass size={16} weight="bold" /> Filtrar</button>
          {(showResults || search || cityFilter || stateFilter) && (
            <button className="btn btn-ghost text-xs" onClick={clearFilters} data-testid="clear-filters"><X size={12} /> Limpar</button>
          )}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button className="btn btn-ghost" onClick={() => exportFile("xlsx")} data-testid="export-xlsx"><FileXls size={16} weight="bold" /> Excel</button>
          <button className="btn btn-ghost" onClick={() => exportFile("pdf")} data-testid="export-pdf"><FilePdf size={16} weight="bold" /> PDF</button>
        </div>
      </div>

      {/* Distribuição por estado — SÓ EXIBE quando não há resultados de busca ativos */}
      {!showResults && (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-[1px] bg-black border border-black mb-6" data-testid="state-chart-panel">
        <div className="lg:col-span-3 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-zinc-500">DISTRIBUIÇÃO POR ESTADO</div>
              <div className="font-head text-xl font-bold">Onde estão nossos clientes</div>
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <MapPin size={16} weight="bold" />
              <span className="font-mono">{fmtInt(byState.total)} clientes</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            {chartData.length === 0 ? (
              <div className="text-center text-zinc-400 py-16">Sem dados de estado. Importe do Bling.</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="45%"
                    cy="50%"
                    outerRadius={110}
                    label={(e) => `${e.name} ${e.pct}%`}
                    labelLine={false}
                  >
                    {chartData.map((entry, i) => (
                      <Cell key={entry.name} fill={UF_COLORS[i % UF_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ border: "1px solid #000", borderRadius: 0, fontFamily: "IBM Plex Mono" }}
                    formatter={(v, _n, p) => [`${v} (${p.payload.pct}%)`, p.payload.name]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="lg:col-span-2 bg-white p-6 overflow-auto" data-testid="state-ranking">
          <div className="overline text-zinc-500 mb-3">RANKING</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black">
                <th className="text-left py-2">#</th>
                <th className="text-left">UF</th>
                <th className="text-right">Clientes</th>
                <th className="text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {(byState.by_state || []).slice(0, 15).map((r, i) => (
                <tr
                  key={r.state}
                  className={`border-b border-zinc-100 cursor-pointer hover:bg-yellow-50 ${stateFilter === r.state ? "bg-yellow-50" : ""}`}
                  onClick={() => { setStateFilter(r.state); setShowResults(true); setTimeout(load, 50); }}
                  data-testid={`state-row-${r.state}`}
                >
                  <td className="py-1 text-xs text-zinc-400">{i + 1}</td>
                  <td className="font-mono font-bold">{r.state}</td>
                  <td className="text-right font-mono">{fmtInt(r.count)}</td>
                  <td className="text-right font-mono text-zinc-500">{r.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-zinc-400 mt-3">Clique numa UF para filtrar</p>
        </div>
      </div>
      )}

      {/* Estado inicial: nada aplicado + sem exibir lista → mensagem explicativa */}
      {!showResults && (
        <div className="border border-dashed border-zinc-300 bg-white p-8 text-center text-zinc-500 mb-4" data-testid="customers-hint">
          <MagnifyingGlass size={28} weight="thin" className="mx-auto mb-2 text-zinc-300" />
          <div className="font-semibold text-zinc-700">Use os filtros acima para localizar clientes.</div>
          <div className="text-xs mt-1">Digite nome, cidade ou UF e clique em <b>Filtrar</b> para ver a lista.</div>
        </div>
      )}

      {/* Desktop: tabela tradicional — só aparece após filtrar */}
      {showResults && (
      <div className="hidden md:block border border-black bg-white overflow-x-auto" data-testid="customers-results-desktop">
        <div className="p-3 border-b border-black flex items-center justify-between text-xs text-zinc-600">
          <div>
            <b className="font-mono">{items.length}</b> cliente(s) encontrado(s)
            {(search || cityFilter || stateFilter) && (
              <span className="ml-2 text-zinc-500">
                • filtro:
                {search && <span className="ml-1 font-mono">"{search}"</span>}
                {cityFilter && <span className="ml-1 font-mono">{cityFilter}</span>}
                {stateFilter && <span className="ml-1 font-mono">{stateFilter}</span>}
              </span>
            )}
          </div>
        </div>
        <table className="table" data-testid="customers-table">
          <thead><tr>
            <th>Nome</th><th>Documento</th><th>Cidade/UF</th><th>Email</th><th>Telefone</th><th className="text-center">Pedidos</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-zinc-400">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10">
                <UserList size={40} weight="thin" className="mx-auto text-zinc-300 mb-2" />
                <div className="overline text-zinc-400 mb-1">SEM RESULTADOS</div>
                <div className="text-zinc-500 text-sm">Nenhum cliente encontrado para o filtro aplicado.</div>
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} className="cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                <td className="font-semibold">{c.name}</td>
                <td className="font-mono text-xs">{c.document || "—"}</td>
                <td className="text-zinc-600 text-xs">{c.city ? `${c.city}/${c.state}` : "—"}</td>
                <td className="text-zinc-600 text-xs">{c.email || "—"}</td>
                <td className="font-mono text-xs">{c.cellphone || c.phone || "—"}</td>
                <td className="text-center font-mono font-bold">{fmtInt(c.orders_count || 0)}</td>
                <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button className="text-xs underline mr-3" onClick={() => navigate(`/customers/${c.id}`)} data-testid={`cust-view-${c.id}`}>Ver <ArrowRight size={12} className="inline" /></button>
                  <button className="text-xs underline mr-3" onClick={() => openEdit(c)}><PencilSimple size={12} className="inline mr-1" />Editar</button>
                  <button className="text-xs underline text-red-600" onClick={() => del(c.id)}><Trash size={12} className="inline mr-1" />Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Mobile: cards empilhados — só aparecem após filtrar */}
      {showResults && (
      <div className="md:hidden space-y-3" data-testid="customers-cards-mobile">
        {loading && (
          <div className="text-center py-10 text-zinc-400 border border-zinc-200 bg-white">
            Carregando…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-10 border border-zinc-200 bg-white">
            <UserList size={40} weight="thin" className="mx-auto text-zinc-300 mb-2" />
            <div className="overline text-zinc-400 mb-1">SEM RESULTADOS</div>
            <div className="text-zinc-500 text-sm px-4">
              Nenhum cliente encontrado para o filtro aplicado.
            </div>
          </div>
        )}
        {!loading &&
          items.map((c) => (
            <CustomerCardMobile
              key={c.id}
              customer={c}
              onOpen={(id) => navigate(`/customers/${id}`)}
              onEdit={openEdit}
              onDelete={del}
            />
          ))}
      </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="glass max-w-3xl w-full p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="customer-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-zinc-500">{editing === "new" ? "NOVO CLIENTE" : "EDITAR CLIENTE"}</div>
                <h2 className="font-head text-2xl font-black">{editing === "new" ? "Cadastrar" : form.name || "—"}</h2>
              </div>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="label">Nome / Razão Social *</label>
                  <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" />
                </div>
                <div>
                  <label className="label">CPF / CNPJ</label>
                  <input className="input font-mono" value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} data-testid="cust-document" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Email</label>
                  <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" />
                </div>
                <div>
                  <label className="label">Telefone fixo</label>
                  <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label className="label">Celular</label>
                  <input className="input" value={form.cellphone} onChange={(e) => setForm({ ...form, cellphone: e.target.value })} data-testid="cust-cell" />
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="overline text-zinc-500 mb-2">ENDEREÇO</div>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-8"><label className="label">Endereço</label><input className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div className="col-span-2"><label className="label">Nº</label><input className="input" value={form.address_number} onChange={(e) => setForm({ ...form, address_number: e.target.value })} /></div>
                  <div className="col-span-2"><label className="label">CEP</label><input className="input" value={form.zipcode} onChange={(e) => setForm({ ...form, zipcode: e.target.value })} /></div>
                  <div className="col-span-4"><label className="label">Complemento</label><input className="input" value={form.complement} onChange={(e) => setForm({ ...form, complement: e.target.value })} /></div>
                  <div className="col-span-4"><label className="label">Bairro</label><input className="input" value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} /></div>
                  <div className="col-span-3"><label className="label">Cidade</label><input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div className="col-span-1"><label className="label">UF</label><input className="input" maxLength="2" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} /></div>
                </div>
              </div>
              <div>
                <label className="label">Observações</label>
                <textarea className="input min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" className="btn btn-ghost flex-1" onClick={() => setEditing(null)}>Cancelar</button>
                <button type="submit" className="btn flex-1" data-testid="cust-save"><CheckCircle size={16} weight="bold" /> Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
